import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { TestEnvironment, TestEnvironmentKind } from "@zworks/console-contracts";
import * as React from "react";
import { PageHeader } from "@/components/bespoke";
import { EmptyState } from "@/components/states";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCurrentProject } from "@/lib/project";

const kindLabels: Record<TestEnvironmentKind, string> = {
  local: "Local",
  staging: "Staging",
  preview: "Preview per pull request",
  production: "Production",
};

/** A preview names the pull request it belongs to; no other kind does. */
function kindBadge(environment: TestEnvironment) {
  if (environment.kind === "preview") {
    return <Badge variant="info">Pull request {environment.pullRequestNumber}</Badge>;
  }
  return <Badge variant="outline">{kindLabels[environment.kind]}</Badge>;
}

/** Status is always a labelled badge. Colour never carries the meaning alone. */
function statusBadge(environment: TestEnvironment) {
  if (!environment.enabled) return <Badge variant="muted">Off</Badge>;
  if (environment.kind === "production" && !environment.approvedBy) {
    return <Badge variant="warning">Needs approval</Badge>;
  }
  return <Badge variant="success">Ready</Badge>;
}

function accessBadge(environment: TestEnvironment) {
  return environment.readOnly ? (
    <Badge variant="outline">Read-only</Badge>
  ) : (
    <Badge variant="outline">Read and write</Badge>
  );
}

interface EnvironmentPayload {
  environments?: TestEnvironment[];
}

export function EnvironmentsPage() {
  const { project, loaded } = useCurrentProject();
  const projectId = project?.id;
  const queryClient = useQueryClient();
  const [dialog, setDialog] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [name, setName] = React.useState("");
  const [kind, setKind] = React.useState<TestEnvironmentKind>("staging");
  const [baseUrl, setBaseUrl] = React.useState("");
  const [pullRequestNumber, setPullRequestNumber] = React.useState("");
  const [credentialRefs, setCredentialRefs] = React.useState("");

  const environmentsQuery = useQuery({
    queryKey: ["projects", projectId, "quality", "environments"],
    queryFn: async () => {
      if (!projectId) return [];
      const res = await fetch(`/api/v1/projects/${projectId}/quality/environments`, {
        method: "GET",
        credentials: "same-origin",
      });
      if (!res.ok) return [];
      const body = (await res.json()) as EnvironmentPayload | TestEnvironment[];
      return Array.isArray(body) ? body : (body.environments ?? []);
    },
    enabled: loaded && projectId !== null,
  });

  function closeDialog() {
    setDialog(false);
    setName("");
    setKind("staging");
    setBaseUrl("");
    setPullRequestNumber("");
    setCredentialRefs("");
    setError(null);
  }

  async function refresh() {
    await queryClient.invalidateQueries({
      queryKey: ["projects", projectId, "quality", "environments"],
    });
  }

  async function createEnvironment() {
    if (!projectId) return;
    setError(null);
    const refs = credentialRefs
      .split(",")
      .map((ref) => ref.trim())
      .filter((ref) => ref.length > 0);
    const body: Record<string, unknown> = { name, kind, baseUrl, credentialRefs: refs };
    if (kind === "preview" && pullRequestNumber) {
      body.pullRequestNumber = Number(pullRequestNumber);
    }
    const res = await fetch(`/api/v1/projects/${projectId}/quality/environments`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const problem = (await res.json().catch(() => null)) as { detail?: string } | null;
      setError(problem?.detail ?? "The environment could not be created.");
      return;
    }
    closeDialog();
    await refresh();
  }

  async function setEnabled(environment: TestEnvironment, enabled: boolean) {
    if (!projectId) return;
    setError(null);
    const res = await fetch(
      `/api/v1/projects/${projectId}/quality/environments/${environment.id}`,
      {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      },
    );
    if (!res.ok) {
      const problem = (await res.json().catch(() => null)) as { detail?: string } | null;
      setError(problem?.detail ?? "The environment could not be changed.");
      return;
    }
    await refresh();
  }

  async function approve(environment: TestEnvironment) {
    if (!projectId) return;
    setError(null);
    const res = await fetch(
      `/api/v1/projects/${projectId}/quality/environments/${environment.id}/approval`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ approved: true }),
      },
    );
    if (!res.ok) {
      const problem = (await res.json().catch(() => null)) as { detail?: string } | null;
      setError(problem?.detail ?? "The environment could not be approved.");
      return;
    }
    await refresh();
  }

  if (!loaded || projectId === null || project === null) {
    return (
      <EmptyState
        title="Environments"
        description="Create a project to start managing test environments."
        action={{
          label: "Create Project",
          onClick: () => setError(null),
        }}
      />
    );
  }

  const environments = environmentsQuery.data ?? [];

  return (
    <div className="w-full p-6 max-w-6xl mx-auto">
      <PageHeader
        title="Environments"
        description={project.name}
        actions={<Button onClick={() => setDialog(true)}>New environment</Button>}
      />

      {error ? (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="mt-6">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Test environments</caption>
              <thead>
                <tr>
                  {[
                    "Environment",
                    "Kind",
                    "Address",
                    "Test accounts",
                    "Access",
                    "Status",
                    "Actions",
                  ].map((header) => (
                    <th
                      key={header}
                      scope="col"
                      className="h-10 border-b px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {environments.map((environment) => (
                  <tr key={environment.id} className="group hover:bg-muted/50">
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      <b>{environment.name}</b>
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {kindBadge(environment)}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-mono text-[0.8125rem]">
                      {environment.baseUrl}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {environment.credentialRefs.length > 0 ? (
                        environment.credentialRefs.map((ref) => (
                          <Badge key={ref} variant="secondary" className="mr-1">
                            {ref}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-xs">None</span>
                      )}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {accessBadge(environment)}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {statusBadge(environment)}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      <div className="flex gap-2">
                        {environment.kind === "production" && !environment.approvedBy ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => approve(environment)}
                          >
                            Approve
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEnabled(environment, !environment.enabled)}
                        >
                          {environment.enabled ? "Turn off" : "Turn on"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {environments.length === 0 ? (
            <EmptyState
              title="No environments yet"
              description="Add staging, a preview per pull request, or production. Test accounts stay in Integrations."
              action={{ label: "New environment", onClick: () => setDialog(true) }}
            />
          ) : null}
        </CardContent>
      </Card>

      {dialog ? (
        <Dialog open={true} onOpenChange={(open) => !open && closeDialog()}>
          <DialogContent className="px-6 pb-4 max-w-2xl">
            <DialogTitle>New environment</DialogTitle>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <Input
                  placeholder="Staging"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Kind</label>
                <Select
                  value={kind}
                  onValueChange={(value) => setKind(value as TestEnvironmentKind)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select kind" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="local">Local</SelectItem>
                    <SelectItem value="staging">Staging</SelectItem>
                    <SelectItem value="preview">Preview per pull request</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <Input
                  placeholder="https://staging.example.test"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                />
              </div>
              {kind === "preview" ? (
                <div>
                  <label className="block text-sm font-medium mb-1">Pull request</label>
                  <Input
                    placeholder="42"
                    value={pullRequestNumber}
                    onChange={(e) => setPullRequestNumber(e.target.value)}
                  />
                </div>
              ) : null}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Test accounts</label>
              <Input
                placeholder="test-user, test-password"
                value={credentialRefs}
                onChange={(e) => setCredentialRefs(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button onClick={createEnvironment}>Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
